import React from "react";
import PropTypes from "prop-types";
import { FiChevronUp, FiChevronDown } from "react-icons/fi";
import { useDispatch, useSelector } from "react-redux";

import {
  deleteAxis,
  duplicateAxis,
  selectRetrofitConfiguration,
  setCurrentAxisId,
  setCurrentFiltersStepperStep,
  setShowAxisPopup,
  selectShowDeleteConfirmationPopup,
  setShowDeleteConfirmationPopup,
  selectCurrentAxisId,
  setFetchedBundles
} from "../../../Retrofit/store/slices/retrofit";
import t from "../../../Common/utils/translation/translator";
import { logEvent } from "../../../Common/utils/api/api";
import MsxIcons from "../../../Common/components/Icons/MsxIcons/MsxIcons";
import CustomPopup from "../../../Common/components/CustomPopup/CustomPopup";
import { selectEventsData, updateConfiguration } from "../../../Common/store/slices/common";

const BundleRecap = ({ axis, editingMode }) => {
  const dispatch = useDispatch();
  const eventsData = useSelector(selectEventsData);
  const configuration = useSelector(selectRetrofitConfiguration);
  const currentAxisId = useSelector(selectCurrentAxisId);

  const handleSelectAxis = (e) => {
    e.stopPropagation();
    currentAxisId !== axis.id && dispatch(setFetchedBundles([]));
    dispatch(setCurrentAxisId(axis.id));
    if (editingMode) {
      // dispatch(setCurrentFiltersStepperStep(2));
      dispatch(setCurrentFiltersStepperStep(1));
    } else {
      logEvent(eventsData.locale, eventsData.types.startSelection, eventsData.tags.none, configuration?.configId);
      dispatch(setCurrentFiltersStepperStep(1));
    }
    dispatch(setShowAxisPopup(true));
  };

  const handleDuplicateAxis = () => {
    logEvent(eventsData.locale, eventsData.types.duplicateAxis, eventsData.tags.none, configuration?.configId);
    dispatch(duplicateAxis(axis));
    dispatch(updateConfiguration());
  };

  const handleDeleteAxis = (id) => {
    logEvent(eventsData.locale, eventsData.types.deleteAxis, eventsData.tags.none, configuration?.configId);
    dispatch(deleteAxis(id));
    dispatch(setShowDeleteConfirmationPopup(false));
    dispatch(updateConfiguration());
  };

  const showDeleteConfirmationPopup = useSelector(selectShowDeleteConfirmationPopup);

  return (
    <div className="col-span-3 flex items-center md:border-l border-gray-200 border-solid md:py-2 md:px-4 print:hidden">
      <>
        <div className="flex items-center justify-between md:space-x-2 w-full print:hidden font-bold">
          <div className="flex items-center text-xs">
            {editingMode ? (
              <div className="hidden lg:flex lg:visible items-center space-x-1 text-secondary">
                <MsxIcons name="check_filled" size="nano" />
                <div>{t("msizer.ui.select.bundle_selected")}</div>
              </div>
            ) : (
              <div className="hidden md:flex md:visible items-center space-x-1 text-gray-500">
                <MsxIcons name="alert_rounded" size="nano" />
                <div>{t("msizer.ui.select.no_bundle_selected")}</div>
              </div>
            )}
          </div>
          <div className="flex-1 flex justify-between md:justify-end space-x-2">
            {!editingMode && (
              <qds-button
                size="small"
                id={`specify-button-apply-${axis.id}`}
                onClick={(e) => handleSelectAxis(e)}
                text={t("msizer.ui.select.select_product_bundle_button_text")}
              ></qds-button>
            )}
            {editingMode && (
              <qds-button
                size="small"
                id={`specify-button-change-${axis.id}`}
                onClick={(e) => handleSelectAxis(e)}
                text={t("msizer.ui.select.change_product_bundle_button_text")}
              ></qds-button>
            )}
            <qds-button
              size="small"
              id={`specify-button-duplicate-${axis.id}`}
              title={t("msizer.ui.tooltip.duplicate_axis")}
              onClick={(e) => {
                e.stopPropagation();
                handleDuplicateAxis();
              }}
              icon-name="pages-double"
            ></qds-button>
            <qds-button
              size="small"
              id={`specify-button-delete-${axis.id}`}
              title={t("msizer.ui.tooltip.delete_axis")}
              {...(configuration?.configData.axes.length <= 1 ? { disabled: "" } : {})}
              onClick={(e) => {
                e.stopPropagation();
                dispatch(setCurrentAxisId(axis?.id));
                dispatch(setShowDeleteConfirmationPopup(true));
              }}
              icon-name="trash-can"
            ></qds-button>
            <CustomPopup
              opened={showDeleteConfirmationPopup}
              onClose={() => dispatch(setShowDeleteConfirmationPopup(false))}
              title={t("msizer.ui.popup.delete.axis.confirmation.title")}
              text={`${t("msizer.ui.popup.delete.axis.confirmation.text")} ${
                configuration?.configData?.axes?.find((a) => a.id === currentAxisId)?.name
              }.`}
              cancelLabel={t("msizer.ui.popup.button.cancel")}
              cancelAction={
                () => dispatch(setShowDeleteConfirmationPopup(false))
                // eslint-disable-next-line react/jsx-curly-newline
              }
              continueLabel={t("msizer.ui.popup.button.confirm")}
              continueAction={(e) => {
                e.stopPropagation();
                handleDeleteAxis(currentAxisId);
              }}
            />
            <div
              className={`mx-2 hidden md:flex md:visible items-center print:hidden ${
                !axis.bundle?.motorReference && "opacity-20 cursor-not-allowed"
              }`}
            >
              {axis.collapsed && <FiChevronDown className="text-gray-500" />}
              {!axis.collapsed && <FiChevronUp className="text-gray-500" />}
            </div>
          </div>
        </div>
      </>
    </div>
  );
};

BundleRecap.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  axis: PropTypes.any.isRequired,
  editingMode: PropTypes.bool
};

// define default props
BundleRecap.defaultProps = {
  editingMode: false
};

export default BundleRecap;
